class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this);
    this.submenuFocusCleanups = new Map();
    this.closeAnimationCleanups = new Map();

    if (navigator.platform === 'iPhone') document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    // Handle Escape before the generic details listener can close a level.
    this.addEventListener('keyup', this.onKeyUp.bind(this), true);
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick));
    // Apply already closes the outer summary through its existing inline action.
    this.querySelectorAll('.mobile-facets__close-button').forEach(button => button.addEventListener('click', this.onCloseButtonClick));
  }

  onKeyUp(event) {
    if((event.key || event.code).toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    event.preventDefault();
    event.stopPropagation();

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isClosing = this.closeAnimationCleanups.has(detailsElement);
    const isOpen = detailsElement.hasAttribute('open') && !isClosing;
    if (isClosing) {
      event.preventDefault();
      this.cancelCloseAnimation(detailsElement);
      detailsElement.setAttribute('open', '');
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else if (isOpen) {
      event.preventDefault();
      this.closeSubmenu(detailsElement);
    } else {
      this.cancelSubmenuFocus(detailsElement);
      const panel = summaryElement.nextElementSibling;
      const openingFocus = document.activeElement;
      let focusFrame;
      let focusStarted;
      const abandonOpening = () => {
        this.cancelSubmenuFocus(detailsElement);
        detailsElement.classList.remove('menu-opening');
        detailsElement.removeAttribute('open');
        summaryElement.setAttribute('aria-expanded', false);
        const activeElement = document.activeElement;
        if (this.mainDetailsToggle.classList.contains('menu-opening') &&
            activeElement?.closest('details[open]') === this.mainDetailsToggle) {
          trapFocus(this.mainDetailsToggle, activeElement);
        }
      };
      const focusPanel = (time) => {
        focusStarted ??= time;
        if (!detailsElement.hasAttribute('open') || !this.mainDetailsToggle.classList.contains('menu-opening')) {
          this.cancelSubmenuFocus(detailsElement);
          return;
        }
        const activeElement = document.activeElement;
        const focusInsidePanel = panel.contains(activeElement);
        if (activeElement !== summaryElement && activeElement !== openingFocus && !focusInsidePanel) {
          abandonOpening();
          return;
        }
        const focusTarget = focusInsidePanel ? activeElement : detailsElement.querySelector('.mobile-facets__close-button');
        if (focusTarget?.getClientRects().length && getComputedStyle(focusTarget).visibility === 'visible') {
          trapFocus(panel, focusTarget);
          if (panel.contains(document.activeElement)) {
            parentMenuElement && parentMenuElement.classList.add('submenu-open');
            this.cancelSubmenuFocus(detailsElement);
            return;
          }
        }
        if (time - focusStarted >= 600) {
          abandonOpening();
          return;
        }
        focusFrame = window.requestAnimationFrame(focusPanel);
      };
      const openingTimer = setTimeout(() => {
        if (!detailsElement.hasAttribute('open') || !this.mainDetailsToggle.classList.contains('menu-opening')) {
          this.cancelSubmenuFocus(detailsElement);
          return;
        }
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        focusFrame = window.requestAnimationFrame(focusPanel);
      }, 100);
      this.submenuFocusCleanups.set(detailsElement, () => {
        clearTimeout(openingTimer);
        window.cancelAnimationFrame(focusFrame);
      });
    }
  }

  cancelSubmenuFocus(detailsElement) {
    this.submenuFocusCleanups.get(detailsElement)?.();
    this.submenuFocusCleanups.delete(detailsElement);
  }

  cancelCloseAnimation(detailsElement) {
    this.closeAnimationCleanups.get(detailsElement)?.();
    this.closeAnimationCleanups.delete(detailsElement);
  }

  openMenuDrawer(summaryElement) {
    this.cancelCloseAnimation(this.mainDetailsToggle);
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open')) this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      this.cancelSubmenuFocus(details);
      this.cancelCloseAnimation(details);
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector('summary')?.setAttribute('aria-expanded', false);
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer(event);
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    this.cancelSubmenuFocus(detailsElement);
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    if (this.closeAnimationCleanups.has(detailsElement)) return;
    let animationStart;
    let animationFrame;
    const cancelAnimation = () => window.cancelAnimationFrame(animationFrame);
    this.closeAnimationCleanups.set(detailsElement, cancelAnimation);

    const handleAnimation = (time) => {
      if (this.closeAnimationCleanups.get(detailsElement) !== cancelAnimation) return;
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        animationFrame = window.requestAnimationFrame(handleAnimation);
      } else {
        this.cancelCloseAnimation(detailsElement);
        detailsElement.removeAttribute('open');
        const parentDetails = detailsElement.parentElement.closest('details[open]');
        const activeElement = document.activeElement;
        if (parentDetails?.classList.contains('menu-opening') &&
            activeElement?.closest('details[open]') === parentDetails) {
          // Restore the parent trap without undoing navigation during closing.
          trapFocus(parentDetails, activeElement);
        }
      }
    }

    animationFrame = window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);
